import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { Folder } from 'types';

import { dateFormatInverted } from 'constants/formats';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import errorSwal from 'utils/errorSwal';

import { useTranslate } from 'hooks/useTranslate';
import { useGetDocuements } from 'modules/documents/hooks/useGetDocuments';
import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';

import { documentsListInitValue } from '../constants/documentsListInitValue';
import { DocumentsListContextType } from '../types/DocumentsListContextType';

import { UploadDocumentModalProvider } from '../../Modals/UploadDocumentModal/context/UploadDocumentModalContext';
import UploadDocumentModal from '../../Modals/UploadDocumentModal';

const DocumentsListContext = createContext<DocumentsListContextType>(
  documentsListInitValue
);

export const DocumentsListProvider: FC<{
  children: ReactNode;
  section: string;
  currentFolder?: Folder;
}> = ({ children, section, currentFolder }) => {
  const { t } = useTranslate();
  const { societyId } = useParams();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const user = useSelector((state: State) => state.user);

  const documents = useSelector((state: State) => state.documents || []);

  const [page, setPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [refetchKey, setRefetchKey] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useGetDocuements({
    societyId,
    folderId: currentFolder?._id,
    page,
    itemsPerPage,
    refetchKey,
  });

  const invalidateDocuments = () => {
    setRefetchKey((v) => v + 1);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeItemsPerPage = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setPage(1);
  };

  const handleAddDocument = () => {
    store.dispatch(
      setModal(
        <UploadDocumentModalProvider
          folder={currentFolder}
          onUploadDocument={invalidateDocuments}
        >
          <UploadDocumentModal />
        </UploadDocumentModalProvider>
      )
    );
  };

  const uploadFile = async (file: File) => {
    const societyId = actualSociety['_id'];
    const newDocument = {
      file,
      date: format(new Date(), dateFormatInverted),
      size: file?.size,
      name: file?.name,
      author: user?.['_id'],
      society: societyId,
      fileType: fileTypes[file?.type as keyof typeof fileTypes],
      category: documentTypes.DOCUMENTS,
      subcategory: '',
      description: t('DocumentLinkedManually'),

      folder: currentFolder?._id,
    };
    await store.dispatch(addDocument(newDocument));
  };

  const handleUploadFiles = async (files: File[]) => {
    setIsLoading(true);
    try {
      await Promise.all(files.map((file) => uploadFile(file)));

      invalidateDocuments();
    } catch (error) {
      errorSwal(t('ErrorUploadingDocuments'));
    }
    setIsLoading(false);
  };

  return (
    <DocumentsListContext.Provider
      value={{
        data: {
          totalDocuments: currentFolder?.documentsCount.total || 0,
          documents:
            documents?.filter((doc) => doc.folder === currentFolder?._id) || [],
          section,
          currentFolderId: currentFolder?._id,
          isAdmin: true,
          isReadOnly: false,
          isLoading,
        },
        actions: {
          handleChangePage,
          handleChangeItemsPerPage,
          handleAddDocument,
          handleOpenDocument: () => {},
          handleDeleteDocument: () => {},
          handleUploadFiles,
        },
      }}
    >
      {children}
    </DocumentsListContext.Provider>
  );
};

export const useDocumentsList = () => {
  const context = useContext(DocumentsListContext);
  if (!context) {
    throw new Error(
      'useDocumentsList must be used within a DocumentsListProvider'
    );
  }
  return context;
};
